// This API is meant for internal use only between `UIIFrame` and `UIEmbeddedComponent`

import type {
  AnyMessage,
  BroadcastMessage,
  EmbeddedContext,
  EmbeddedSendMessageContext,
  HostContext,
  HostSendMessageContext,
  MessageCallback,
  ReadyMessage,
  SyncMessage,
  AnyPayload,
  EmbeddedPropsChangedMessage,
} from '../types';
import {
  createBroadcastMessage,
  createBroadcastReplyMessage,
  createDirectedMessage,
  createMessage,
  createReplyMessage,
  isOfType,
  TO_ALL,
  validFormat,
} from './IFrameMessage';

type SendMessageFunction = (
  message: AnyMessage,
  callback?: MessageCallback
) => void;

export const IFRAME_HOST = 'HOST' as const;

export const MSG_TYPE_SYNC = 'SYNC' as const;
export const MSG_TYPE_SYNC_ACK = 'SYNC_ACK' as const;
export const MSG_TYPE_READY = 'READY' as const;
export const MSG_TYPE_EMBEDDED_PROPS_CHANGED =
  'EMBEDDED_PROPS_CHANGED' as const;

const createHostSendMessageContext = (
  embedId: string,
  sendMessageFn: SendMessageFunction
): HostSendMessageContext => ({
  sendMessage: (
    type: string,
    data: AnyPayload = {},
    callback?: MessageCallback
  ): void => {
    const message = createMessage(type, data, {
      to: embedId,
      from: IFRAME_HOST,
    });
    sendMessageFn(message, callback);
  },

  sendReplyMessage: (
    replyToMessage: AnyMessage,
    type: string,
    data: AnyPayload = {},
    callback?: MessageCallback
  ): void => {
    const message = createReplyMessage(replyToMessage, type, data);
    sendMessageFn(message, callback);
  },
});

const createEmbeddedSendMessageContext = (
  embedId: string,
  sendMessageFn: SendMessageFunction,
  defaultGroup: string
): EmbeddedSendMessageContext => ({
  sendMessage: (
    type: string,
    data: AnyPayload = {},
    callback?: MessageCallback
  ): void => {
    const message = createMessage(type, data, {
      to: IFRAME_HOST,
      from: embedId,
    });
    sendMessageFn(message, callback);
  },

  sendReplyMessage: (
    replyToMessage: AnyMessage,
    type: string,
    data: AnyPayload = {},
    callback?: MessageCallback
  ): void => {
    const message = createReplyMessage(replyToMessage, type, data);
    sendMessageFn(message, callback);
  },

  sendDirectedMessage: (
    type: string,
    data: AnyPayload = {},
    to: string,
    callback?: MessageCallback
  ): void => {
    const message = createDirectedMessage(type, data, { to, from: embedId });
    sendMessageFn(message, callback);
  },

  sendBroadcastMessage: (
    type: string,
    data?: AnyPayload,
    group: string = defaultGroup
  ): void => {
    const message = createBroadcastMessage(type, data, {
      from: embedId,
      group,
    });
    sendMessageFn(message);
  },

  sendBroadcastReplyMessage: (
    replyToMessage: BroadcastMessage,
    type: string,
    data?: AnyPayload
  ): void => {
    const message = createBroadcastReplyMessage(
      replyToMessage,
      type,
      data,
      embedId
    );
    sendMessageFn(message);
  },
});

export const isSyncMessage = (message: AnyMessage): message is SyncMessage =>
  isOfType(message, MSG_TYPE_SYNC);

export const isReadyMessage = (message: AnyMessage): message is ReadyMessage =>
  isOfType(message, MSG_TYPE_READY);

export const isEmbeddedPropsChangedMessage = (
  message: AnyMessage
): message is EmbeddedPropsChangedMessage =>
  isOfType(message, MSG_TYPE_EMBEDDED_PROPS_CHANGED);

export const isToIFrameHost = (message: AnyMessage): boolean =>
  message.to === IFRAME_HOST || message.to === TO_ALL;

export const createAppData = (info: any, name: string) => ({
  info,
  name,
  url: window.location.href,
});

export const createHostContext = (
  embedId: string,
  sendMessageFn: SendMessageFunction
): HostContext => ({
  id: embedId,
  ...createHostSendMessageContext(embedId, sendMessageFn),
});

interface EmbedContextFactoryParams {
  embedId: string;
  group: string;
  name: string;
  app: any;
  sendMessageFn: SendMessageFunction;
}

export const createEmbeddedContext = ({
  embedId,
  group,
  name,
  app,
  sendMessageFn,
}: EmbedContextFactoryParams): EmbeddedContext => ({
  id: embedId,
  group,
  name,
  app,
  ...createEmbeddedSendMessageContext(embedId, sendMessageFn, group),
});

interface SyncMessageFactoryParams {
  embedId: string;
  group: string;
  name: string;
  appData: any;
  embeddedProps: any;
}

export const createSyncMessage = ({
  embedId,
  group,
  name,
  appData,
  embeddedProps,
}: SyncMessageFactoryParams): SyncMessage =>
  createMessage(
    MSG_TYPE_SYNC,
    { embedId, group, name, appData, embeddedProps },
    { to: embedId, from: IFRAME_HOST }
  );

export const createSyncAckMessage = (syncMessage: AnyMessage) =>
  createReplyMessage(syncMessage, MSG_TYPE_SYNC_ACK, {});

export const createReadyMessage = (from: string, data = {}): ReadyMessage =>
  createMessage(MSG_TYPE_READY, data, {
    to: IFRAME_HOST,
    from,
  });

export const createEmbeddedPropsChangeMessage = (
  embedId: string,
  embeddedProps: AnyPayload
): EmbeddedPropsChangedMessage =>
  createMessage(MSG_TYPE_EMBEDDED_PROPS_CHANGED, embeddedProps, {
    to: embedId,
    from: IFRAME_HOST,
  });

export const validMessage = (
  { origin, data }: MessageEvent,
  expectedOrigin: string | null
): boolean => origin === expectedOrigin && validFormat(data);
